import { Rectangle } from '@phosphor-icons/react';
import { Button, colors, VText } from '@vake/ui-kit';
import { FC, ReactElement, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import styled from 'styled-components';
import { useAuth } from '../../../../auth/AuthProvider';
import { downloadCsv, useIsAdmin, useSendToMattermost } from '../../../../requests/reports';
import constants from '../../../../shared/constants';
import { CircleDot } from '../../../../shared/styles/CircleDot';
import { Spinner } from '../../../../shared/styles/Spinner';
import { Insights } from '../../../../utils/interfaces';
import { BorderWrapper as Border, ColumnWrapper, PaddingWrapper, RoundedWrapper } from '../../styles/Wrappers';
import { Arrow, ArrowWrapper, IReportInfo } from './ReportInfo';

const DetectionsWrapper = styled(PaddingWrapper)`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
`;

const List = styled.ul`
    list-style-type: none;
    margin: 0;
`;
const Secondary = styled(PaddingWrapper)<{ border?: boolean }>`
    flex-direction: row;
    background: #002540;
    border-bottom: ${(props) => props.border && '1px solid rgba(255, 255, 255, 0.2)'};
    border-radius: ${(props) => !props.border && '0 0 5px 5px'};
    cursor: pointer;
    align-items: center;
    &:hover {
        box-shadow: inset 0 0 100px 100px rgba(255, 255, 255, 0.1);
    }
    img {
        max-width: 16px;
        max-height: 16px;
        margin-left: 30px;
    }
`;

interface IMoreInfo extends IReportInfo {
    reportName: string;
    insights: Insights | undefined;
    lastItem: boolean;
    nextLayer: () => void;
    prevLayer: () => void;
    chosenDate: string;
    visibleLayers: string[];
    setVisibleLayers: (layers: string[]) => void;
    reportHasBegan: boolean;
}

interface ILegend {
    id: string;
    text: string;
    tooltip?: string;
    active: boolean;
    icon: string | ReactElement;
    visibleLayers: string[];
    setVisibleLayers: (layers: string[]) => void;
}

export const MoreInfo: FC<IMoreInfo> = ({
    index,
    lastItem,
    availableDates,
    reportName,
    prevLayer,
    nextLayer,
    chosenDate,
    insights,
    visibleLayers,
    setVisibleLayers,
    setProcessingStatus,
    detectionConfidence,
    numAcqusitions,
    reportHasBegan,
}) => {
    const [downloadingCsv, setDownloadingCsv] = useState<boolean>(false);
    const [visibleMetaData, setVisibleMeta] = useState<boolean>(false);
    const { mutate } = useSendToMattermost();
    const { token } = useAuth();
    const { data: isAdmin } = useIsAdmin(token);

    let shipDetected = 0;
    let areDetectionsCorrelated = false;
    let darkShipsNumber = 0;
    let unCorrelatedAisNumber = 0;
    if (insights) {
        if (insights.undetected_ais) {
            const uncorrelatedAis = insights.undetected_ais.features;
            unCorrelatedAisNumber = uncorrelatedAis.length;
        }
        if (insights.matched_vessels) {
            areDetectionsCorrelated = true;
            const matchedVessels = insights.matched_vessels.features;
            shipDetected = matchedVessels.length;
        }
        if (insights.dark_vessels) {
            const darkShips = insights.dark_vessels.features.filter(
                (feature) => feature.properties?.object_class_score >= detectionConfidence / 100
            );
            darkShipsNumber = darkShips.length;
        }
    }
    const legendColor = constants.colors.markers;
    const aquisitionLayer = {
        id: 'acqusition_data',
        text: `${numAcqusitions} incoming satellite pass` + (numAcqusitions > 1 ? 'es' : ''),
        tooltip: 'Planned satellite passes in the next couple of hours.',
        active: numAcqusitions > 0,
        icon: <CircleDot {...legendColor.acquisitions} />,
        visibleLayers: visibleLayers,
        setVisibleLayers: setVisibleLayers,
    };
    const legends: ILegend[] = [
        {
            id: `matched_vessels`,
            text: `${shipDetected} ${areDetectionsCorrelated ? 'verified AIS' : 'ships detected'}`,
            tooltip: 'All detections that has been matched with an AIS transciever',
            active: Boolean(shipDetected),
            icon: <CircleDot {...legendColor.matchedVessels} />,
            visibleLayers: visibleLayers,
            setVisibleLayers: setVisibleLayers,
        },
        {
            id: `dark_vessels`,
            text: areDetectionsCorrelated ? `${darkShipsNumber} dark vessels` : 'AIS correlation not run',
            tooltip: 'All detections that could not be matched to a known vessel',
            active: Boolean(darkShipsNumber),
            icon: <CircleDot {...legendColor.darkShips} />,
            visibleLayers: visibleLayers,
            setVisibleLayers: setVisibleLayers,
        },
        {
            id: `undetected_ais`,
            text: `${unCorrelatedAisNumber} uncorrelated AIS`,
            tooltip: 'All AIS transcievers that could not be matched to a detection',
            active: Boolean(unCorrelatedAisNumber),
            icon: <CircleDot {...legendColor.undetectedAis} />,
            visibleLayers: visibleLayers,
            setVisibleLayers: setVisibleLayers,
        },
    ];
    if (numAcqusitions > 0) {
        legends.push(aquisitionLayer);
    }
    const params = useParams();
    const date = availableDates[index];
    return (
        <RoundedWrapper background="#0061A6">
            {!reportHasBegan && <>Nothing to view</>}
            {reportHasBegan && (availableDates === undefined || availableDates.length === 0) && <Spinner />}
            {reportHasBegan && availableDates.length > 0 && (
                <>
                    <DetectionsWrapper>
                        <ColumnWrapper id="current_date">
                            <VText>{chosenDate}</VText>
                        </ColumnWrapper>
                        <ArrowWrapper id="date_navigator">
                            <Arrow disabled={index === 0} onClick={() => prevLayer()}>
                                <img src="/images/icons/ArrowLeft.svg" alt="Previous date" />
                            </Arrow>
                            <Arrow disabled={lastItem} onClick={() => nextLayer()}>
                                <img src="/images/icons/ArrowRight.svg" alt="Next date" />
                            </Arrow>
                        </ArrowWrapper>
                    </DetectionsWrapper>
                    <Border id="legend">
                        <div className="flex flex-col">
                            {legends.map((legend, i) => (
                                <Legend key={`legend_${i}`} {...legend} />
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <Link
                                to={`/actor/${params.id}?startDate=${chosenDate}&endDate=${chosenDate}&confidence=${detectionConfidence}`}
                            >
                                <Button
                                    text="Go to Vessel Table"
                                    size="small"
                                    onClick={() => {
                                        mutate(
                                            `User clicked Actor for ${reportName} on date ${
                                                date.toISOString().split('T')[0]
                                            } for [this site](${window.location.href})`
                                        );
                                    }}
                                />
                            </Link>
                            {
                                <Link
                                    to={`/v2/overwatch/${params.id}?startDate=${chosenDate}&confidence=${detectionConfidence}`}
                                >
                                    <Button
                                        text="Go to Overwatch 2.0"
                                        size="small"
                                        onClick={() => {
                                            mutate(
                                                `User clicked Overwatch 2.0 for ${reportName} on date ${
                                                    date.toISOString().split('T')[0]
                                                } for [this site](${window.location.href})`
                                            );
                                        }}
                                    />
                                </Link>
                            }
                        </div>
                    </Border>
                    <Secondary
                        id="show_metadata"
                        onClick={() => {
                            setVisibleMeta(!visibleMetaData);
                            setProcessingStatus((showProcessingStatus) => !showProcessingStatus);
                        }}
                        border
                    >
                        <VText>View processing status</VText>
                    </Secondary>
                    {visibleMetaData && (
                        <Border style={{ background: '#002540' }}>
                            <List>
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <Rectangle size={16} color={colors.green[100]} />
                                        <VText>Successfully processed</VText>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <Rectangle size={16} color={colors.yellow[100]} />
                                        <VText>Currently being processed</VText>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <Rectangle size={16} color={colors.blue[100]} />
                                        <VText>In processing queue</VText>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <Rectangle size={16} color={colors.red[100]} />
                                        <VText>Processing failed</VText>
                                    </div>
                                </div>
                            </List>
                        </Border>
                    )}
                    <Secondary
                        id="download_csv"
                        onClick={async () => {
                            setDownloadingCsv(true);
                            const name = `Vake Ship Detection Report ${reportName} ${date.toISOString().split('T')[0]}`;
                            await downloadCsv(name, params, date);
                            mutate(
                                `User downloaded CSV for report ${reportName} on date ${
                                    date.toISOString().split('T')[0]
                                }`
                            );
                            setDownloadingCsv(false);
                        }}
                    >
                        <VText>Download full dataset as CSV-file</VText>
                        {downloadingCsv && <Spinner />}
                    </Secondary>
                </>
            )}
        </RoundedWrapper>
    );
};

const Legend = (props: ILegend) => {
    const { id, text, tooltip, active, icon, setVisibleLayers, visibleLayers } = props;

    const toggleLayerVisibility = () => {
        if (!id) return; // Ensure the id exists
        setVisibleLayers(
            visibleLayers.includes(id) ? visibleLayers.filter((layer: string) => layer !== id) : [...visibleLayers, id]
        );
    };

    return (
        <>
            <div id={id} onClick={toggleLayerVisibility} className="flex items-center gap-2 hover:cursor-pointer">
                {typeof icon === 'string' ? <img src={icon} alt="" className="h-5 w-5" /> : icon}

                <VText
                    significance={visibleLayers.includes(id) ? 'primary' : 'secondary'}
                    data-tooltip-content={tooltip}
                >
                    {text}
                </VText>

                <Tooltip anchorId={`test_${id}`} place="right" />
            </div>
        </>
    );
};
